/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Cell, colors, Grid, HeadingSection, Theme, VFlow } from 'bold-ui'
import theme from 'config/theme'
import { Fragment, useMemo } from 'react'
import { MetaPath } from 'util/metaPath'

import { AlteracaoFenotipicaView, AlteracoesFenotipicasModel } from './alteracao-fenotipica/AlteracaoFenotipicaView'
import { PeuriculturaAlert } from './components/PuericulturaAlert'
import { FatoresRiscoModel, FatoresRiscoView } from './fatores-risco/FatoresRiscoView'
import MarcosDesenvolvimentoView, { MarcosDesenvolvimentoModel } from './marcos/MarcosDesenvolvimentoView'
import PrenatalPartoNascimento, {
  PrenatalPartoNascimentoModel,
} from './prenatal-parto-nascimento/PrenatalPartoNascimento'
import { idadeMaiorQue } from './util/idadeMaiorQue'

export interface PuericulturaModel {
  prenatal?: PrenatalPartoNascimentoModel
  fatoresRisco?: FatoresRiscoModel
  alteracoesFenotipicas?: AlteracoesFenotipicasModel
  marcosDesenvolvimento?: MarcosDesenvolvimentoModel
}

export interface PuericulturaViewProps {
  name: MetaPath<PuericulturaModel>
  cidadaoDataNascimento: LocalDate
  prontuarioId: ID
}

const LIMITE_IDADE_AVALIACAO_DESENVOLVIMENTO = 10

export default function PuericulturaView(props: PuericulturaViewProps) {
  const { name, cidadaoDataNascimento, prontuarioId } = props
  const classes = useMemo(() => createStyles(theme), [])

  const acessoAvaliarDesenvolvimento = useMemo(
    () => !idadeMaiorQue(cidadaoDataNascimento, LIMITE_IDADE_AVALIACAO_DESENVOLVIMENTO),
    [cidadaoDataNascimento]
  )

  return (
    <VFlow style={classes.container}>
      <PrenatalPartoNascimento
        name={name.prenatal}
        cidadaoDataNascimento={cidadaoDataNascimento}
        prontuarioId={prontuarioId}
      />
      {acessoAvaliarDesenvolvimento ? (
        <Grid style={classes.border}>
          <Cell size={12}>
            <Fragment>
              <HeadingSection level={3} title='Desenvolvimento da criança' />
              <VFlow>
                <PeuriculturaAlert name={name} />
                <AlteracaoFenotipicaView name={name.alteracoesFenotipicas} prontuarioId={prontuarioId} />
                <FatoresRiscoView name={name.fatoresRisco} prontuarioId={prontuarioId} />
                <MarcosDesenvolvimentoView name={name.marcosDesenvolvimento} prontuarioId={prontuarioId} />
              </VFlow>
            </Fragment>
          </Cell>
        </Grid>
      ) : (
        <Alert type='info' inline>
          Após os 10 anos de idade, as informações de desenvolvimento da criança permanecem somente no acompanhamento da
          criança.
        </Alert>
      )}
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  border: css`
    border-top: 1px solid ${theme.pallete.divider};
  `,
  container: css`
    margin-top: 0.5rem;
    padding: 1rem;
    border: 1px ${colors.turquoise.c60} solid;
    border-radius: ${theme.radius.input}px;
  `,
})
